import { useEffect, useState, useCallback } from 'react';
import { FIREBASE_VAPID_KEY } from '../constants/firebase';
import { getMessagingInstance, getToken, onMessage } from '../lib/firebase';
import { ProfileApi } from '../services/api/apis/profile/profile.api';
import { api } from '../services/api/api';
import { useIsLoggedIn } from './useIsLoggedIn';

// Create a ProfileApi instance for use in this hook
const profileApi = new ProfileApi(api);

interface UsePushNotificationsReturn {
  /** Current notification permission state */
  permission: NotificationPermission | 'unsupported';
  /** Whether push notifications are supported in this browser */
  isSupported: boolean;
  /** Request permission and register for push notifications */
  requestPermission: () => Promise<NotificationPermission | 'unsupported'>;
  /** Whether the hook is currently loading/initializing */
  isLoading: boolean;
}

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isLoggedIn = useIsLoggedIn();

  // Check if notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported =
        typeof window !== 'undefined' &&
        'Notification' in window &&
        'serviceWorker' in navigator;

      setIsSupported(supported);

      if (supported)
        setPermission(Notification.permission);
      else
        setPermission('unsupported');

      setIsLoading(false);
    };

    checkSupport();
  }, []);

  // Register FCM token with backend
  const registerToken = useCallback(async () => {
    try {
      const messaging = getMessagingInstance();
      if (!messaging || !FIREBASE_VAPID_KEY)
        return;

      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;

      const token = await getToken(messaging, {
        vapidKey: FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token)
        await profileApi.updateDeviceToken({ deviceToken: token, platform: 'web' });
    } catch {
      // Silently handle errors - push notifications are not critical
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<NotificationPermission | 'unsupported'> => {
    if (!isSupported)
      return 'unsupported';

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted')
        await registerToken();

      return result;
    } catch {
      return 'denied';
    }
  }, [isSupported, registerToken]);

  // Listen for foreground messages
  useEffect(() => {
    const browserPermission = typeof window !== 'undefined' && 'Notification' in window
      ? Notification.permission
      : 'denied';

    if (!isSupported || browserPermission !== 'granted')
      return;

    const messaging = getMessagingInstance();
    if (!messaging)
      return;

    const unsubscribe = onMessage(messaging, payload => {
      const { title, body } = payload.notification || {};
      const { redirectUrl } = payload.data || {};

      if (Notification.permission === 'granted' && title) {
        // Use service worker to show notification (more reliable)
        void navigator.serviceWorker.ready.then(registration => {
          void registration.showNotification(title, {
            body: body || '',
            icon: '/512x512.png',
            badge: '/favicon-32x32.png',
            data: { redirectUrl },
            tag: 'foreground-notification',
            requireInteraction: false,
          });
        }).catch(() => {
          // Fallback to regular Notification API
          try {
            const notification = new Notification(title, {
              body: body || '',
              icon: '/512x512.png',
            });
            notification.onclick = () => {
              window.focus();
              if (redirectUrl)
                window.location.href = redirectUrl;
              notification.close();
            };
          } catch {
            // Notification failed - ignore
          }
        });
      }
    });

    return () => unsubscribe();
  }, [isSupported, permission]);

  // Auto-register token when user logs in and permission is granted
  useEffect(() => {
    if (isLoggedIn && permission === 'granted' && !isLoading)
      void registerToken();

  }, [isLoggedIn, permission, isLoading, registerToken]);

  return {
    permission,
    isSupported,
    requestPermission,
    isLoading,
  };
};
